import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { 
  MoreVertical, Trash2, Megaphone, ThumbsUp, MessageCircle, 
  ChevronDown, ChevronUp, Send, Loader2
} from "lucide-react";
import { getGroupPostComments, createGroupPostComment, resolveImageUrl } from "@/lib/api";
import LikesDialog from "@/components/LikesDialog";

export function formatRelativeDate(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export default function GroupPost({ post, groupId, currentUserId, isAdmin, onDelete, onLike }) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [likesDialogOpen, setLikesDialogOpen] = useState(false);

  const loadComments = async () => {
    setLoadingComments(true);
    try {
      const res = await getGroupPostComments(groupId, post.post_id);
      setComments(res.data.comments || []);
    } catch {
      toast.error("Failed to load comments");
    }
    setLoadingComments(false);
  };

  const handleToggleComments = () => {
    if (!showComments) {
      loadComments();
    }
    setShowComments(!showComments);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    setSubmitting(true);
    try {
      const res = await createGroupPostComment(groupId, post.post_id, { content: newComment });
      setComments([...comments, res.data]);
      setNewComment("");
    } catch {
      toast.error("Failed to add comment");
    }
    setSubmitting(false);
  };

  return (
    <Card className={`bg-white border-[#E5E0D5] ${post.is_announcement ? 'ring-2 ring-[#D4AF37] ring-opacity-50' : ''}`}>
      <CardContent className="p-4">
        {/* Announcement Badge */}
        {post.is_announcement && (
          <div className="flex items-center gap-2 mb-3 pb-3 border-b border-[#E5E0D5]">
            <Megaphone className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-sm font-semibold text-[#D4AF37] font-['Nunito']">Announcement</span>
          </div>
        )}

        {/* Author Info */}
        <div className="flex items-start gap-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={resolveImageUrl(post.author_picture)} />
            <AvatarFallback className="bg-[var(--liturgical-primary)] text-white">
              {post.author_name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-['Nunito'] font-medium text-[#2D2A26]">{post.author_name}</span>
                <span className="text-xs text-[#6B665E] font-['Nunito'] ml-2">{formatRelativeDate(post.created_at)}</span>
              </div>
              
              {/* Delete option for author or admin */}
              {(post.author_id === currentUserId || isAdmin) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white">
                    <DropdownMenuItem onClick={() => onDelete(post.post_id)} className="text-red-600">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Post
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            
            {/* Post Content */}
            <p className="text-[#2D2A26] font-['Nunito'] mt-2 whitespace-pre-wrap">{post.content}</p>
            
            {/* Post Image */}
            {post.image_url && (
              <img 
                src={resolveImageUrl(post.image_url)} 
                alt="Post" 
                className="mt-3 rounded-lg max-h-80 object-cover w-full"
              />
            )}
            
            {/* Actions */}
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#E5E0D5]">
              <div className="flex items-center">
                <button 
                  onClick={() => onLike(post.post_id)}
                  className={`flex items-center gap-1 text-sm font-['Nunito'] transition-colors ${
                    post.user_liked ? 'text-[var(--liturgical-primary)]' : 'text-[#6B665E] hover:text-[var(--liturgical-primary)]'
                  }`}
                  data-testid={`like-post-${post.post_id}`}
                >
                  <ThumbsUp className={`w-4 h-4 ${post.user_liked ? 'fill-current' : ''}`} />
                </button>
                {(post.like_count || 0) > 0 ? (
                  <button
                    className={`text-sm font-['Nunito'] ml-1 transition-colors hover:underline cursor-pointer ${post.user_liked ? 'text-[var(--liturgical-primary)]' : 'text-[#6B665E] hover:text-[var(--liturgical-primary)]'}`}
                    onClick={() => setLikesDialogOpen(true)}
                    data-testid={`like-count-${post.post_id}`}
                  >
                    {post.like_count}
                  </button>
                ) : (
                  <span className="text-sm font-['Nunito'] ml-1 text-[#6B665E]">0</span>
                )}
              </div>
              
              <button 
                onClick={handleToggleComments}
                className="flex items-center gap-1 text-sm text-[#6B665E] hover:text-[#2D2A26] font-['Nunito'] transition-colors"
                data-testid={`toggle-comments-${post.post_id}`}
              >
                <MessageCircle className="w-4 h-4" />
                {post.comment_count || 0}
                {showComments ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            </div>
            
            {/* Comments Section */}
            {showComments && (
              <div className="mt-3 pt-3 border-t border-[#E5E0D5]">
                {loadingComments ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-[var(--liturgical-primary)]" />
                  </div>
                ) : (
                  <>
                    {comments.map(comment => (
                      <div key={comment.comment_id} className="flex gap-2 mb-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={resolveImageUrl(comment.author_picture)} />
                          <AvatarFallback className="bg-[#556B2F] text-white text-xs">
                            {comment.author_name?.[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 bg-[#F9F7F2] rounded-lg p-2">
                          <span className="font-['Nunito'] font-medium text-sm text-[#2D2A26]">{comment.author_name}</span>
                          <p className="text-sm text-[#2D2A26] font-['Nunito']">{comment.content}</p>
                        </div>
                      </div>
                    ))}
                    
                    {/* Add Comment */}
                    <form onSubmit={handleSubmitComment} className="flex gap-2 mt-3">
                      <Input
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Write a comment..."
                        className="flex-1 border-[#E5E0D5] text-sm"
                        data-testid={`comment-input-${post.post_id}`}
                      />
                      <Button 
                        type="submit" 
                        size="sm" 
                        disabled={submitting || !newComment.trim()}
                        className="bg-[var(--liturgical-primary)] hover:brightness-90"
                        data-testid={`submit-comment-${post.post_id}`}
                      >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
      <LikesDialog postId={post.post_id} open={likesDialogOpen} onOpenChange={setLikesDialogOpen} />
    </Card>
  );
}